// Curated PostgreSQL keyword list emitted by `pgsqlResolver`. Tier 7 per
// RESEARCH.md §5.2. The orchestrator's `applyKeywordCasingPostProcess`
// transforms label/insertText/filterText to the user's preferred casing —
// labels stay UPPER here so the dominant-case fallback has a stable
// reference and matches the conventional rendering.
//
// Selection criteria: PG keywords that surface in normal user authoring
// flows (DML, DDL, query-shaping, transactional control, common modern
// PG forms like RETURNING / LATERAL / WINDOW). Reserved-but-rare words
// that only appear in system-generated DDL are excluded.

const PG_KEYWORDS: readonly string[] = [
  'ALL', 'ALTER', 'ANALYZE', 'AND', 'ANY', 'ARRAY', 'AS', 'ASC',
  'ASYMMETRIC', 'AT', 'AUTHORIZATION', 'BEGIN', 'BETWEEN', 'BOOLEAN',
  'BOTH', 'BY', 'CALL', 'CASE', 'CAST', 'CHECK', 'COALESCE', 'COLLATE',
  'COLUMN', 'COMMIT', 'CONCURRENTLY', 'CONFLICT', 'CONSTRAINT', 'CREATE',
  'CROSS', 'CTID', 'CURRENT', 'CURRENT_DATE', 'CURRENT_SCHEMA',
  'CURRENT_TIME', 'CURRENT_TIMESTAMP', 'CURRENT_USER', 'CYCLE', 'DATA',
  'DATABASE', 'DECLARE', 'DEFAULT', 'DEFERRABLE', 'DELETE', 'DESC',
  'DISTINCT', 'DO', 'DROP', 'ELSE', 'END', 'EXCEPT', 'EXCLUDED', 'EXISTS',
  'EXPLAIN', 'EXTENSION', 'FALSE', 'FETCH', 'FILTER', 'FIRST',
  // PL/pgSQL EXCEPTION condition names (surfaced after `WHEN` inside an
  // EXCEPTION block). PG documents the full list under "Trapping Errors";
  // we curate the most common ones so a `RAISE`/`CHECK` author gets
  // tier-7 keyword hints without leaning on Monaco wordBased fallback.
  'FOREIGN_KEY_VIOLATION',
  'FOR',
  'FOREIGN', 'FROM', 'FULL', 'FUNCTION', 'GRANT', 'GROUP', 'GROUPING',
  'HAVING', 'IF', 'ILIKE', 'IN', 'INDEX', 'INHERIT', 'INNER', 'INSERT',
  // Common PG data-type keywords surfaced at DDL `data-type` positions
  // (CREATE TABLE column type, CREATE DOMAIN AS …, function parameter
  // types, etc.). The grammar maps these slots to `'other'`/`'unknown'`
  // so resolver-side keyword emission is the carrier here.
  'INTEGER',
  'INTERSECT', 'INTO', 'IS', 'ISNULL',
  // PG JSON type keywords — surfaced at data-type positions.
  'JSON', 'JSONB',
  'JOIN', 'KEY', 'LANGUAGE', 'LAST',
  'LATERAL', 'LEADING', 'LEFT', 'LIKE', 'LIMIT', 'MATERIALIZED', 'MERGE',
  'NATURAL', 'NEW',
  // PL/pgSQL EXCEPTION condition name — most common single-row error.
  'NO_DATA_FOUND',
  'NOT', 'NOTHING', 'NOTNULL', 'NULL', 'NULLIF', 'NULLS',
  'NUMERIC',
  'OFFSET', 'OLD', 'ON', 'ONLY', 'OPERATOR', 'OR', 'ORDER', 'OTHERS',
  'OUTER', 'OVER', 'OVERLAPS', 'PARTITION', 'PERFORM', 'PRECEDING',
  'PRIMARY', 'PROCEDURE', 'RAISE_EXCEPTION', 'RECURSIVE', 'REFERENCES',
  'REFRESH', 'REPLACE', 'RESET',
  'RETURNING', 'RETURNS', 'REVOKE', 'RIGHT', 'ROLE', 'ROLLBACK', 'ROW',
  'ROWS', 'SCHEMA', 'SECURITY', 'SELECT', 'SEQUENCE', 'SESSION_USER',
  'SET', 'SHOW', 'SIMILAR', 'SOME', 'STORED', 'SYMMETRIC', 'TABLE',
  'TEMP', 'TEMPORARY', 'TEXT', 'THEN', 'TIES',
  // Date/time data-type keywords.
  'TIMESTAMP', 'TIMESTAMPTZ',
  'TO', 'TRAILING', 'TRIGGER',
  'TRUE', 'TRUNCATE', 'TYPE', 'UNBOUNDED', 'UNION', 'UNIQUE',
  // PL/pgSQL EXCEPTION condition name — UNIQUE/PK violation.
  'UNIQUE_VIOLATION',
  'UNLOGGED',
  'UPDATE', 'USER', 'USING',
  // `VALUE` pseudo-identifier surfaced in `CREATE DOMAIN … CHECK (…)`
  // bodies — PG binds it to the value being checked.
  'VALUE',
  'VALUES',
  // Variable-length character type — emitted at data-type positions.
  'VARCHAR',
  'VARIADIC', 'VERBOSE', 'VIEW',
  'WHEN', 'WHERE', 'WINDOW', 'WITH', 'WITHIN', 'WITHOUT',
];

export const PGSQL_KEYWORDS: ReadonlySet<string> = new Set(PG_KEYWORDS);

/**
 * Position-aware keyword list. Today returns the whole curated set — the
 * orchestrator's quality-filter trims the popup. A future per-position
 * refinement can split this into clause-start vs general subsets.
 */
export function getPgsqlKeywordsForPosition(): readonly string[] {
  return PG_KEYWORDS;
}

/**
 * Multi-word PostgreSQL keywords typed one word at a time — see
 * `compoundKeywordRemainder.ts`. Only compounds
 * `context/legalNextKeywords.ts` does not already carry for pgsql belong here.
 * Sources (PG docs, "Window Function Calls" — frame clause):
 *   • `UNBOUNDED FOLLOWING` — frame END bound; the per-slot model carries only
 *     the frame-START vocabulary (`after-window-frame-unit`), and the
 *     direction-scoped lists live in `pgsqlResolver.ts`
 *     (`PGSQL_FRAME_START_KEYWORDS` / `PGSQL_FRAME_END_KEYWORDS`).
 *   • `NO OTHERS` — the default `EXCLUDE` option
 *     (`PGSQL_FRAME_EXCLUDE_KEYWORDS`).
 */
export const PGSQL_COMPOUND_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'NO OTHERS',
];
